"use client";
import style from './headerActions.module.scss';
import Button from '@/projectRoot/src/components/common/form-elements/button/Button';
import SearchIcon from '@/projectRoot/src/components/common/svg/SearchIcon';
import LinkUrl from '@/projectRoot/src/components/common/general/link/LinkUrl';
import Image from 'next/image';
import ProfileIcon from '@/projectRoot/src/components/common/svg/ProfileIcon';
import HeartIcon from '@/projectRoot/src/components/common/svg/HeartIcon';
import CartBag from '@/projectRoot/src/components/common/svg/Cart';
import { useGlobals } from '@/projectRoot/src/contexts/GlobalsContext';
import { routeUrl } from '@/projectRoot/src/utils/handlers/urlHandler';
import { locales } from '@/projectRoot/routes.config';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { translationsHandler } from '@/projectRoot/src/utils/handlers/translationsHandler';
import { useEffect, useState } from 'react';
import { Link } from '@/projectRoot/src/i18n/navigation';
import Drawer from '@/projectRoot/src/components/common/drawer/Drawer';
import { useCart } from '@/projectRoot/src/contexts/CartContext';
import { useProfile } from '@/projectRoot/src/contexts/ProfileContext';

interface Props {
    onClickShowSidebar?: () => void;
    visibleSidebar?: boolean;
    hovered?: boolean;
    classes?: string[];
    multilang_slugs?: Record<string, string>;
    route_parameter?: {
        list?: string;
        single?: string;
    };
}

const HeaderActions = ({onClickShowSidebar, visibleSidebar, hovered, classes = [], multilang_slugs, route_parameter}: Props) => {
    const {isMobileView} = useGlobals();
    const {identity, logout} = useProfile();
    const {cartShortcut, showCartShortcut, cart} = useCart();
    const currentLang = useLocale();
    const params = useParams();
    const pathname = usePathname();
    const query = useSearchParams();
    const t = useTranslations();
    const [openLocales, setOpenLocales] = useState<boolean>(false);
    const [showAuth, setShowAuth] = useState<boolean>(false);
    const [showIdentityLinks, setShowIdentityLinks] = useState<boolean>(false);
    const lang = useLocale();
    let refinedSlugs: Record<string, string>;
    
    if(!multilang_slugs && !isMobileView){
        refinedSlugs = translationsHandler({
            refinedSlugs: refinedSlugs, 
            pathname: pathname, 
            lang: currentLang, 
            route_parameter: route_parameter ? (Object.keys(params).length > 1 ? route_parameter.single : route_parameter.list) : undefined, 
            params: params as Record<string, string>
        });
    }

    const onClickShowAuth = (show: boolean) => {
        if(isMobileView){
            setShowIdentityLinks(!showIdentityLinks);
        }
        if(identity) return;
        setShowAuth(show);
    }

    useEffect(() => {
        if(typeof window !== 'undefined' && isMobileView){
            window.addEventListener('click', () => {
                setShowIdentityLinks(false);
            });

            return () => {
                window.removeEventListener('click', () => {
                    setShowIdentityLinks(false);
                });
            }
        }
    }, [isMobileView]);

    useEffect(() => {
        if(identity){
            setShowAuth(false);
        }
    }, [identity]);

  return (
    <div className={`${style.headerActions} ${visibleSidebar ? style.colored : ''} ${classes.join(' ')}`}>
        <Button label='toggler' className={`${style.toggler} ${hovered ? style.colored : ''} ${visibleSidebar ? style.changed : ''}`} onClick={onClickShowSidebar}>
            <span></span>
            <span></span>
            <span></span>
        </Button>
        {
            !isMobileView
            ?
            <div className={`${style.locales} ${hovered ? style.hovered : ''} ${openLocales ? style.opened : ''}`}>
                <p className={style.currentLocale}  onClick={() => setOpenLocales(!openLocales)}>
                    {currentLang.toLocaleUpperCase()}
                </p>
                <div className={style.localesList}>
                    {
                        locales.map(locale => {
                            let href: string = routeUrl({
                                lang: locale,
                                multilangslugs: multilang_slugs ? multilang_slugs : refinedSlugs,
                                pathname: !multilang_slugs ? pathname : '',
                                query: !multilang_slugs ? query : [],
                                fake_url: refinedSlugs ? true : false
                            });
                            return (
                                <LinkUrl 
                                    text={locale.toLocaleUpperCase()} 
                                    url={href} 
                                    key={locale} 
                                    classes={[`${style.localeLink} ${locale == currentLang ? style.active : ''}`]} 
                                />
                            )
                        })
                    }
                </div>
            </div>
            : <></>
        }
        <Button label='search' className={style.searchBtn}>
            <SearchIcon width={20} height={20} hexColor={visibleSidebar || hovered ? 'black' : 'white'} />
        </Button>
        <Link className={`${style.logo}`} href={'/'}>
            <Image src={visibleSidebar || hovered ? '/cubes-logo-dark.png' : '/cubes-logo.png'} width={120} height={34} alt='logo' objectFit='contain' />
        </Link>
        <div className={style.profileBtn} onClick={(e) => {e.stopPropagation(); onClickShowAuth(!showAuth);}}>
            <ProfileIcon width={20} height={20} hexColor={visibleSidebar || hovered ? 'black' : 'white'} />
            {
                identity 
                ?
                <div className={`${style.identityLinks} ${showIdentityLinks ? style.visible : ''}`}>
                    <p className={style.identityLink}>
                        {t('hello')} {identity.first_name}
                    </p>
                    <LinkUrl classes={[style.identityLink]} url={`/${lang}/${t('RoutesTranslations.profile').toLocaleLowerCase()}`} text={t('profile')} />
                    <LinkUrl classes={[style.identityLink]} url={`/${lang}/${t('RoutesTranslations.profile/orders').toLocaleLowerCase()}`} text={t('my_orders')} />
                    <Button label={t('logout')} classes={[style.identityLink]} onClick={(e) => {e.stopPropagation(); logout();}} />
                </div>
                : <></>
            }
        </div>
        <LinkUrl 
            classes={[style.wishlistBtn]} 
            url={`/${lang}/${t('RoutesTranslations.profile/wishlist').toLocaleLowerCase()}`} 
            noLink={!identity} 
            onClick={() => !identity ? onClickShowAuth(true) : {}}
        >
            <HeartIcon width={20} height={20} hexColor={visibleSidebar || hovered ? 'black' : 'white'} />
        </LinkUrl>
        <Button label='cart' className={style.cartBtn} onClick={() => showCartShortcut(true)}>
            <CartBag width={20} height={20} hexColor={visibleSidebar || hovered ? 'black' : 'white'} />
           <span style={{color: visibleSidebar || hovered ? 'black' : 'white'}} className={style.counter}>({cart?.items_count ?? 0})</span>
        </Button>
        <Drawer 
            content={''}
            content_type='auth'
            showDrawer={showAuth}
            onCloseDrawer={() => onClickShowAuth(false)}
            title={''}
            content_position='right'
        />
        <Drawer 
            content_type='cart'
            onCloseDrawer={() => showCartShortcut(false)}
            showDrawer={cartShortcut}
            title={t('your_cart')}
            content={''}
        />
    </div>
  )
};

export default HeaderActions;
